import React from 'react';
import _ from 'lodash';
import { styled } from '@mui/material';

import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Divider,
    Icon,
    Stack,
    Text,
    Tooltip,
} from 'shell/components';
import { getUserPreferredFullName } from 'utils/helper';

const RubricSubmissionStack = styled(Stack)(({ theme }) => {
    return {
        padding: theme.spacing(5, 7),
    };
});

const ZeroSumSubmissionGridBox = styled(Box)(({ theme }) => {
    return {
        padding: theme.spacing(5, 7),
        display: 'grid',
        gridTemplateColumns: '20% 1fr 60%',
        rowGap: theme.spacing(7),
    };
});

const ZeroSumMobileStack = styled(Stack)(({ theme }) => {
    return {
        padding: theme.spacing(5, 4),
    };
});

const RubricMemberBox = styled(Box)(({ theme }) => {
    return {
        width: '100%',
    };
});

const RubricRateBox = styled(Box)(({ theme }) => {
    return {
        marginTop: theme.spacing(4),
        display: 'grid',
        gridTemplateColumns: '45% 45%',
        gap: '10%',
    };
});

const RubricTotalStack = styled(Stack)(({ theme }) => {
    return {
        maxWidth: theme.spacing(60),
    };
});

const RubricMobileRateStack = styled(Stack)(({ theme }) => {
    return {
        marginTop: theme.spacing(4),
    };
});

const AlertStack = styled(Stack)(({}) => {
    return {};
});

export const AlertIcon = styled(Icon)(({ dark, theme }) => {
    return {
        borderRadius: '50%',
        padding: '0.3125rem',
        fontSize: '1.5rem',
        background: dark ? 'transparent' : theme.palette.ensightful.error[700],
        color: dark ? theme.palette.ensightful.neutral[50] : theme.palette.ensightful.shades[0],
    };
});

export const ZeroSumGiven = ({ device, selectedStudent, students, submissionList }) => {
    const student = students.find((student) => student.owner_account_id === selectedStudent);
    if (!student) return;
    const findMySubmission = submissionList.find(
        (submission) => submission.owner_account_id === student.owner_account_id
    );
    const submissionData = findMySubmission?.data || {};
    const members = students.filter((member) => member.active && member.group_id === student.group_id);
    return (
        <ZeroSumSubmissionGridBox device={device} key={selectedStudent}>
            <Text variant={'p2'} weight={'medium'}>
                Student
            </Text>
            <Text variant={'p2'} weight={'medium'}>
                Grade Received
            </Text>
            <Text variant={'p2'} weight={'medium'}>
                Comments
            </Text>
            {members.map((member) => {
                const evaluation = submissionData?.[member.owner_account_id];
                return (
                    <>
                        <Text variant={'p2'} color={'light'}>
                            {getUserPreferredFullName(member)}
                        </Text>
                        <Text variant={'p2'} color={'light'}>
                            {evaluation?.rate || 'No submission'}
                        </Text>
                        <Text rich variant={'p2'} color={'light'}>
                            {evaluation?.details || 'No submission'}
                        </Text>
                    </>
                );
            })}
        </ZeroSumSubmissionGridBox>
    );
};

export const ZeroSumReceived = ({
    device,
    isInstructor,
    currentPeerEvaluation,
    selectedStudent,
    students,
    submissionList,
}) => {
    const findZeroSumAvg = (userAccountId, submissionDataFromOtherMembers, members) => {
        const submissions = submissionDataFromOtherMembers?.filter(({ owner_account_id }) =>
            members.find((member) => member.owner_account_id === owner_account_id)
        );
        const accumulatedNumber = submissions?.reduce((acc, cur) => {
            acc = acc + parseInt(cur.data[userAccountId]?.rate || 0);
            return acc;
        }, 0);
        if (accumulatedNumber <= 0) return 0;
        return _.round(accumulatedNumber / submissions?.length, 2);
    };

    const zeroSumRawNum = (userAccountId, submissionDataFromOtherMembers, members) => {
        const submissions = submissionDataFromOtherMembers?.filter(({ owner_account_id }) =>
            members.find((member) => member.owner_account_id === owner_account_id)
        );
        const divider = submissions.length / members?.length || 1;
        const totalPts = 100;
        const accumulatedNumber = submissions?.reduce((acc, cur) => {
            acc = acc + parseInt(cur.data[userAccountId]?.rate || 0);
            return acc;
        }, 0);
        if (accumulatedNumber <= 0) return 0;
        let result = _.round(parseInt(accumulatedNumber) / divider, 2);
        result = result / totalPts;
        if (result > 1) result = 1;

        return result;
    };

    const student = students.find((student) => student.owner_account_id === selectedStudent);

    if (!student) return;
    const submissions = submissionList.filter(
        (submission) =>
            submission.peer_evaluation_id === currentPeerEvaluation.id &&
            submission.group_id === student.group_id &&
            submission?.data?.[student.owner_account_id]
    );
    const members = students.filter((member) => member.active && member.group_id === student.group_id);
    let isNotInATeam = false;
    submissions.forEach((submission) => {
        Object.keys(submission.data).forEach((memberId) => {
            const findAccountId = members.find((member) => member.owner_account_id === memberId);
            if (!findAccountId) {
                isNotInATeam = true;
            }
        });
    });

    return (
        <ZeroSumSubmissionGridBox device={device} key={selectedStudent}>
            <Text variant={'p2'} weight={'medium'}>
                Student
            </Text>
            <Text variant={'p2'} weight={'medium'}>
                Grade Received
            </Text>
            <Text variant={'p2'} weight={'medium'}>
                Comments
            </Text>
            {members.map((member, index) => {
                const submission = submissions.find(
                    (submission) => submission.owner_account_id === member.owner_account_id
                );
                if (submission) {
                }
                const memberSubmission = submission?.data?.[student.owner_account_id];
                let name =
                    student.owner_account_id === member.owner_account_id
                        ? 'Self Evaluation'
                        : getUserPreferredFullName(member);
                if (
                    currentPeerEvaluation?.keep_result_anonymous &&
                    student.owner_account_id !== member.owner_account_id &&
                    !isInstructor
                ) {
                    name = `Anonymous ${index + 1}`;
                }
                return (
                    <>
                        <Text variant={'p2'} color={'light'}>
                            {name}
                        </Text>
                        <Text variant={'p2'} color={'light'}>
                            {memberSubmission?.rate || 'No submission'}
                        </Text>
                        <Text rich variant={'p2'} color={'light'}>
                            {memberSubmission?.details || 'No submission'}
                        </Text>
                    </>
                );
            })}
            <Stack alignItems={'center'} direction={'row'} spacing={2}>
                <Text variant={'p2'}>Avg</Text>
                {isNotInATeam && (
                    <Tooltip
                        maxWidth={160}
                        title={
                            <Stack divider={<Divider />} spacing={1}>
                                <Text color={'inherit'} variant={'p3'}>
                                    Students were moved from this group after this average was calculated was
                                    calculated, as a result it is no longer accurate. Please manually review the average
                                    for this team for your records.
                                </Text>
                            </Stack>
                        }
                        type={'alert'}
                    />
                )}
            </Stack>
            <Text variant={'p2'}>{findZeroSumAvg(selectedStudent, submissions, members)}</Text>
            <Text></Text>
            <Text variant={'p2'}>Raw #</Text>
            <Text data-cy={'student-zerosum-raw-number'} variant={'p2'}>
                {zeroSumRawNum(selectedStudent, submissions, members)}
            </Text>
        </ZeroSumSubmissionGridBox>
    );
};

export const ZeroSumMobileGiven = ({ device, selectedStudent, students, submissionList }) => {
    const student = students.find((student) => student.owner_account_id === selectedStudent);
    if (!student) return;
    const findMySubmission = submissionList.find(
        (submission) => submission.owner_account_id === student.owner_account_id
    );
    const submissionData = findMySubmission?.data || {};
    const members = students.filter((member) => member.active && member.group_id === student.group_id);
    return (
        <ZeroSumMobileStack device={device} key={selectedStudent} spacing={6}>
            {members.map((member) => {
                const evaluation = submissionData?.[member.owner_account_id];
                return (
                    <Stack key={member.id} spacing={2}>
                        <Text variant={'p2'}>{getUserPreferredFullName(member)}</Text>
                        <Text variant={'p2'}>{`Grade Recieved: ${evaluation?.rate || 'No submission'}`}</Text>
                        <Text rich variant={'p2'} color={'light'}>
                            {evaluation?.details || 'No submission'}
                        </Text>
                    </Stack>
                );
            })}
        </ZeroSumMobileStack>
    );
};

export const ZeroSumMobileReceived = ({
    device,
    isInstructor,
    currentPeerEvaluation,
    selectedStudent,
    students,
    submissionList,
}) => {
    const findZeroSumAvg = (userAccountId, submissionDataFromOtherMembers, members) => {
        const submissions = submissionDataFromOtherMembers?.filter(({ owner_account_id }) =>
            members.find((member) => member.owner_account_id === owner_account_id)
        );
        const accumulatedNumber = submissions?.reduce((acc, cur) => {
            acc = acc + parseInt(cur.data[userAccountId]?.rate || 0);
            return acc;
        }, 0);
        if (accumulatedNumber <= 0) return 0;
        return _.round(accumulatedNumber / submissions?.length, 2);
    };

    const zeroSumRawNum = (userAccountId, submissionDataFromOtherMembers, members) => {
        const submissions = submissionDataFromOtherMembers?.filter(({ owner_account_id }) =>
            members.find((member) => member.owner_account_id === owner_account_id)
        );
        const divider = submissions.length / members?.length || 1;
        const totalPts = 100;
        const accumulatedNumber = submissions?.reduce((acc, cur) => {
            acc = acc + parseInt(cur.data[userAccountId]?.rate || 0);
            return acc;
        }, 0);
        if (accumulatedNumber <= 0) return 0;
        let result = _.round(parseInt(accumulatedNumber) / divider, 2);
        result = result / totalPts;
        if (result > 1) result = 1;

        return result;
    };
    const student = students.find((student) => student.owner_account_id === selectedStudent);
    if (!student) return;
    const submissions = submissionList.filter(
        (submission) =>
            submission.peer_evaluation_id === currentPeerEvaluation.id &&
            submission.group_id === student.group_id &&
            submission?.data?.[student.owner_account_id]
    );
    const members = students.filter((member) => member.active && member.group_id === student.group_id);
    let isNotInATeam = false;
    submissions.forEach((submission) => {
        Object.keys(submission.data).forEach((memberId) => {
            const findAccountId = members.find((member) => member.owner_account_id === memberId);
            if (!findAccountId) {
                isNotInATeam = true;
            }
        });
    });

    return (
        <ZeroSumMobileStack device={device} key={selectedStudent}>
            <Stack spacing={6}>
                {members.map((member, index) => {
                    const submission = submissions.find(
                        (submission) => submission.owner_account_id === member.owner_account_id
                    );
                    const memberSubmission = submission?.data?.[student.owner_account_id];
                    let name =
                        student.owner_account_id === member.owner_account_id
                            ? 'Self Evaluation'
                            : getUserPreferredFullName(member);
                    if (
                        currentPeerEvaluation?.keep_result_anonymous &&
                        student.owner_account_id !== member.owner_account_id &&
                        !isInstructor
                    ) {
                        name = `Anonymous ${index + 1}`;
                    }
                    return (
                        <Stack key={member.id} spacing={2}>
                            <Text variant={'p2'}>{name}</Text>
                            <Text variant={'p2'}>{`Grade Recieved: ${memberSubmission?.rate || 'No submission'}`}</Text>
                            <Text rich variant={'p2'} color={'light'}>
                                {memberSubmission?.details || 'No submission'}
                            </Text>
                        </Stack>
                    );
                })}
            </Stack>
            <Stack sx={{ mt: 6 }} alignItems={'center'} direction={'row'} spacing={2}>
                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                    <Text variant={'p2'}>Avg</Text>
                    {isNotInATeam && (
                        <Tooltip
                            maxWidth={160}
                            title={
                                <Stack divider={<Divider />} spacing={1}>
                                    <Text color={'inherit'} variant={'p3'}>
                                        Students were moved from this group after this average was calculated was
                                        calculated, as a result it is no longer accurate. Please manually review the
                                        average for this team for your records.
                                    </Text>
                                </Stack>
                            }
                            type={'alert'}
                        />
                    )}
                </Stack>
                <Text variant={'p2'}>{findZeroSumAvg(selectedStudent, submissions, members)}</Text>
            </Stack>
            <Text></Text>
            <Stack alignItems={'center'} direction={'row'} spacing={2}>
                <Text variant={'p2'}>Raw #</Text>
                <Text data-cy={'student-zerosum-raw-number'} variant={'p2'}>
                    {zeroSumRawNum(selectedStudent, submissions, members)}
                </Text>
            </Stack>
        </ZeroSumMobileStack>
    );
};

export const RubricReceived = ({
    device,
    isInstructor,
    currentPeerEvaluation,
    selectedStudent,
    students,
    submissionList,
}) => {
    const findRubricAvg = (userAccountId, submissionDataFromOtherMembers, members) => {
        if (!submissionDataFromOtherMembers.length) return 0;
        const submissions = submissionDataFromOtherMembers?.filter(({ owner_account_id }) =>
            members.find((member) => member.owner_account_id === owner_account_id)
        );
        const divider = members?.length > submissions.length ? submissions.length : members?.length;
        const rate = submissions?.reduce((acc, cur) => {
            if (cur?.data?.[userAccountId]?.rubric_data) {
                cur.data[userAccountId].rubric_data.forEach((row) => {
                    const findRate = row.options?.find((option) => option.id === row.selected_rate_id)?.rate;
                    if (findRate) {
                        acc = acc + parseInt(findRate);
                    }
                });
            }

            return acc;
        }, 0);
        const avg = parseInt(rate || 0) / divider;
        return _.round(avg, 2);
    };

    const findRubricRawNum = (userAccountId, submissionDataFromOtherMembers, members) => {
        let totalPts = 0;
        if (!currentPeerEvaluation) return 0;
        currentPeerEvaluation.rubric_data.forEach((row) => {
            const curRowMaxRate = Math.max(...row.options.map((option) => option.rate));
            const maxRate = parseInt(curRowMaxRate);
            totalPts = totalPts + maxRate;
        });
        const submissions = submissionDataFromOtherMembers?.filter(({ owner_account_id }) =>
            members.find((member) => member.owner_account_id === owner_account_id)
        );
        const divider = members?.length > submissions.length ? submissions.length : members?.length;
        const accumulatedNumber = submissions?.reduce((acc, cur) => {
            if (cur?.data?.[userAccountId]?.rubric_data) {
                cur.data[userAccountId].rubric_data.forEach((row) => {
                    const findRate = row.options?.find((option) => option.id === row.selected_rate_id)?.rate;
                    if (findRate) {
                        acc = acc + parseInt(findRate);
                    }
                });
            }

            return acc;
        }, 0);
        if (accumulatedNumber <= 0) return 0;
        let result = _.round(parseInt(accumulatedNumber) / divider, 2);
        result = result / totalPts;
        if (result > 1) result = 1;

        return _.round(result, 2);
    };

    const student = students.find((student) => student.owner_account_id === selectedStudent);
    if (!student) return;
    const submissions = submissionList.filter(
        (submission) =>
            submission.peer_evaluation_id === currentPeerEvaluation.id &&
            submission.group_id === student.group_id &&
            submission?.data?.[student.owner_account_id]
    );
    let maxTotal = currentPeerEvaluation?.rubric_data?.reduce((acc, row) => {
        const curRowMaxRate = Math.max(...row.options.map((option) => option.rate));
        acc = acc + parseInt(curRowMaxRate);
        return acc;
    }, 0);
    let isNotInATeam = false;
    const members = students.filter((member) => member.active && member.group_id === student.group_id);
    submissions.forEach((submission) => {
        Object.keys(submission.data).forEach((memberId) => {
            const findAccountId = members.find((member) => member.owner_account_id === memberId);
            if (!findAccountId) {
                isNotInATeam = true;
            }
        });
    });

    return (
        <RubricSubmissionStack device={device} key={selectedStudent} divider={<Divider />} spacing={7}>
            {members?.map((member, index) => {
                const submission = submissions.find(
                    (submission) => submission.owner_account_id === member.owner_account_id
                );
                const memberSubmission = submission?.data?.[student.owner_account_id];
                let individualTotal = 0;
                let submissionNumber = 0;
                let name =
                    student.owner_account_id === member.owner_account_id
                        ? 'Self Evaluation'
                        : getUserPreferredFullName(member);
                if (
                    currentPeerEvaluation?.keep_result_anonymous &&
                    student.owner_account_id !== member.owner_account_id &&
                    !isInstructor
                ) {
                    name = `Anonymous ${index + 1}`;
                }
                return (
                    <RubricMemberBox key={member.owner_account_id}>
                        <Text variant={'p2'}>{name}</Text>
                        <RubricRateBox>
                            <Stack spacing={2}>
                                <Text variant={'p2'} weight={'medium'}>
                                    Criteria
                                </Text>
                                <Stack spacing={2}>
                                    {memberSubmission?.rubric_data?.map((row, index) => {
                                        let findTitle = currentPeerEvaluation.rubric_data.find(
                                            (orgRow) => orgRow.id === row.id
                                        )?.title;
                                        if (!findTitle) {
                                            findTitle = row.title || 'No Title';
                                        }
                                        const rate =
                                            row.options.find((option) => option.id === row.selected_rate_id)?.rate || 0;
                                        const curRowMaxRate = Math.max(...row.options.map((option) => option.rate));

                                        individualTotal = individualTotal + parseInt(rate);
                                        submissionNumber = submissionNumber + 1;
                                        return (
                                            <Stack
                                                key={member.id + row.id}
                                                alignItems={'center'}
                                                direction={'row'}
                                                justifyContent={'space-between'}
                                                spacing={2}
                                            >
                                                <Text variant={'p2'}>{`C${index + 1} - ${findTitle}`}</Text>
                                                <Text variant={'p2'} color={'light'}>
                                                    {rate
                                                        ? `${_.round(rate, 2) || '0'}/${curRowMaxRate}`
                                                        : 'No submission'}
                                                </Text>
                                            </Stack>
                                        );
                                    }) || (
                                        <Text variant={'p2'} color={'light'}>
                                            No Submission
                                        </Text>
                                    )}
                                    {memberSubmission && (
                                        <Stack
                                            alignItems={'center'}
                                            direction={'row'}
                                            justifyContent={'flex-end'}
                                            spacing={2}
                                        >
                                            <Text variant={'p2'} weight={'medium'}>
                                                Total
                                            </Text>
                                            <Text variant={'p2'}>{`${
                                                _.round(individualTotal, 2) || '0'
                                            }/${maxTotal}`}</Text>
                                        </Stack>
                                    )}
                                </Stack>
                            </Stack>
                            <Stack spacing={2}>
                                <Text variant={'p2'}>Comments</Text>
                                <Text rich variant={'p2'} color={'light'}>
                                    {memberSubmission?.details || 'No submission'}
                                </Text>
                            </Stack>
                        </RubricRateBox>
                    </RubricMemberBox>
                );
            })}
            <Stack>
                <RubricTotalStack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                        <Text variant={'p2'} weight={'medium'}>
                            Individual Average
                        </Text>
                        {isNotInATeam && (
                            <Tooltip
                                maxWidth={160}
                                title={
                                    <Stack divider={<Divider />} spacing={1}>
                                        <Text color={'inherit'} variant={'p3'}>
                                            Students were moved from this group after this average was calculated was
                                            calculated, as a result it is no longer accurate. Please manually review the
                                            average for this team for your records.
                                        </Text>
                                    </Stack>
                                }
                                type={'alert'}
                            />
                        )}
                    </Stack>
                    <Text variant={'p2'} color={'light'}>
                        {`${findRubricAvg(selectedStudent, submissions, members)}/${maxTotal}`}
                    </Text>
                </RubricTotalStack>
                <RubricTotalStack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text variant={'p2'} weight={'medium'}>
                        Team Average
                    </Text>
                    <Text variant={'p2'} color={'light'}>
                        {`${findRubricAvg(selectedStudent, submissions, members)}/${maxTotal}`}
                    </Text>
                </RubricTotalStack>
                <RubricTotalStack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text variant={'p2'} weight={'medium'}>
                        Raw #
                    </Text>
                    <Text data-cy={'student-rubric-raw-number'} variant={'p2'} color={'light'}>
                        {findRubricRawNum(selectedStudent, submissions, members)}
                    </Text>
                </RubricTotalStack>
            </Stack>
        </RubricSubmissionStack>
    );
};

export const RubricGiven = ({ device, currentPeerEvaluation, selectedStudent, students, submissionList }) => {
    const student = students.find((student) => student.owner_account_id === selectedStudent);
    if (!student) return;
    const findMySubmission = submissionList.find(
        (submission) => submission.owner_account_id === student.owner_account_id
    );
    const submissionData = findMySubmission?.data || {};
    const members = students.filter((member) => member.active && member.group_id === student.group_id);
    return (
        <RubricSubmissionStack device={device} key={selectedStudent} divider={<Divider />} spacing={7}>
            {members.map((member) => {
                let individualTotal = 0;
                let submissionNumber = 0;
                let maxTotal = 0;
                const evaluation = submissionData?.[member.owner_account_id];
                return (
                    <RubricMemberBox key={member.owner_account_id}>
                        <Text variant={'p2'}>{getUserPreferredFullName(member)}</Text>
                        <RubricRateBox>
                            <Stack spacing={2}>
                                <Text variant={'p2'} weight={'medium'}>
                                    Criteria
                                </Text>
                                <Stack spacing={2}>
                                    {evaluation?.rubric_data?.map((row, index) => {
                                        let findTitle = currentPeerEvaluation.rubric_data.find(
                                            (orgRow) => orgRow.id === row.id
                                        )?.title;
                                        if (!findTitle) {
                                            findTitle = row.title || 'No Title';
                                        }
                                        const rate =
                                            row.options.find((option) => option.id === row.selected_rate_id)?.rate || 0;
                                        const curRowMaxRate = Math.max(...row.options.map((option) => option.rate));
                                        maxTotal = maxTotal + curRowMaxRate;

                                        individualTotal = individualTotal + parseInt(rate);
                                        submissionNumber = submissionNumber + 1;
                                        return (
                                            <Stack
                                                key={member.id + row.id}
                                                alignItems={'center'}
                                                direction={'row'}
                                                justifyContent={'space-between'}
                                                spacing={2}
                                            >
                                                <Text variant={'p2'}>{`C${index + 1} - ${findTitle}`}</Text>
                                                <Text variant={'p2'} color={'light'}>
                                                    {rate
                                                        ? `${_.round(rate, 2) || '0'}/${curRowMaxRate}`
                                                        : 'No submission'}
                                                </Text>
                                            </Stack>
                                        );
                                    }) || (
                                        <Text variant={'p2'} color={'light'}>
                                            No Submission
                                        </Text>
                                    )}
                                    <Stack
                                        alignItems={'center'}
                                        direction={'row'}
                                        justifyContent={'flex-end'}
                                        spacing={2}
                                    >
                                        <Text variant={'p2'} weight={'medium'}>
                                            Total
                                        </Text>
                                        <Text variant={'p2'}>{`${
                                            _.round(individualTotal, 2) || '0'
                                        }/${maxTotal}`}</Text>
                                    </Stack>
                                </Stack>
                            </Stack>
                            <Stack spacing={2}>
                                <Text variant={'p2'} weight={'medium'}>
                                    Comments
                                </Text>
                                <Text rich variant={'p2'} color={'light'}>
                                    {evaluation?.details || 'No submission'}
                                </Text>
                            </Stack>
                        </RubricRateBox>
                    </RubricMemberBox>
                );
            })}
        </RubricSubmissionStack>
    );
};

export const RubricMobileReceived = ({
    device,
    isInstructor,
    currentPeerEvaluation,
    selectedStudent,
    students,
    submissionList,
}) => {
    const findRubricAvg = (userAccountId, submissionDataFromOtherMembers, members) => {
        if (!submissionDataFromOtherMembers.length) return 0;
        const submissions = submissionDataFromOtherMembers?.filter(({ owner_account_id }) =>
            members.find((member) => member.owner_account_id === owner_account_id)
        );
        const divider = members?.length > submissions.length ? submissions.length : members?.length;
        const rate = submissions?.reduce((acc, cur) => {
            if (cur?.data?.[userAccountId]?.rubric_data) {
                cur.data[userAccountId].rubric_data.forEach((row) => {
                    const findRate = row.options?.find((option) => option.id === row.selected_rate_id)?.rate;
                    if (findRate) {
                        acc = acc + parseInt(findRate);
                    }
                });
            }

            return acc;
        }, 0);
        const avg = parseInt(rate || 0) / divider;
        return _.round(avg, 2);
    };

    const findRubricRawNum = (userAccountId, submissionDataFromOtherMembers, members) => {
        let totalPts = 0;
        if (!currentPeerEvaluation) return 0;
        currentPeerEvaluation.rubric_data.forEach((row) => {
            const curRowMaxRate = Math.max(...row.options.map((option) => option.rate));
            const maxRate = parseInt(curRowMaxRate);
            totalPts = totalPts + maxRate;
        });
        const submissions = submissionDataFromOtherMembers?.filter(({ owner_account_id }) =>
            members.find((member) => member.owner_account_id === owner_account_id)
        );
        const divider = members?.length > submissions.length ? submissions.length : members?.length;
        const accumulatedNumber = submissions?.reduce((acc, cur) => {
            if (cur?.data?.[userAccountId]?.rubric_data) {
                cur.data[userAccountId].rubric_data.forEach((row) => {
                    const findRate = row.options?.find((option) => option.id === row.selected_rate_id)?.rate;
                    if (findRate) {
                        acc = acc + parseInt(findRate);
                    }
                });
            }

            return acc;
        }, 0);
        if (accumulatedNumber <= 0) return 0;
        let result = _.round(parseInt(accumulatedNumber) / divider, 2);
        result = result / totalPts;
        if (result > 1) result = 1;

        return _.round(result, 2);
    };

    const student = students.find((student) => student.owner_account_id === selectedStudent);
    if (!student) return;
    const submissions = submissionList.filter(
        (submission) =>
            submission.peer_evaluation_id === currentPeerEvaluation.id &&
            submission.group_id === student.group_id &&
            submission?.data?.[student.owner_account_id]
    );
    let maxTotal = currentPeerEvaluation?.rubric_data?.reduce((acc, row) => {
        const curRowMaxRate = Math.max(...row.options.map((option) => option.rate));
        acc = acc + parseInt(curRowMaxRate);
        return acc;
    }, 0);
    let isNotInATeam = false;
    const members = students.filter((member) => member.active && member.group_id === student.group_id);
    submissions.forEach((submission) => {
        Object.keys(submission.data).forEach((memberId) => {
            const findAccountId = members.find((member) => member.owner_account_id === memberId);
            if (!findAccountId) {
                isNotInATeam = true;
            }
        });
    });

    return (
        <RubricSubmissionStack device={device} key={selectedStudent}>
            <Stack spacing={6}>
                {members?.map((member, index) => {
                    const submission = submissions.find(
                        (submission) => submission.owner_account_id === member.owner_account_id
                    );
                    const memberSubmission = submission?.data?.[student.owner_account_id];
                    let individualTotal = 0;
                    let submissionNumber = 0;
                    let name =
                        student.owner_account_id === member.owner_account_id
                            ? 'Self Evaluation'
                            : getUserPreferredFullName(member);
                    if (
                        currentPeerEvaluation?.keep_result_anonymous &&
                        student.owner_account_id !== member.owner_account_id &&
                        !isInstructor
                    ) {
                        name = `Anonymous ${index + 1}`;
                    }
                    return (
                        <Accordion key={member.owner_account_id}>
                            <AccordionSummary>
                                <Text variant={'p1'} weight={'semibold'}>
                                    {name}
                                </Text>
                            </AccordionSummary>
                            <AccordionDetails>
                                <RubricMobileRateStack spacing={1}>
                                    <Stack spacing={2}>
                                        <Text variant={'p2'} weight={'medium'}>
                                            Criteria
                                        </Text>
                                        <Stack spacing={2}>
                                            {memberSubmission?.rubric_data?.map((row, index) => {
                                                let findTitle = currentPeerEvaluation.rubric_data.find(
                                                    (orgRow) => orgRow.id === row.id
                                                )?.title;
                                                if (!findTitle) {
                                                    findTitle = row.title || 'No Title';
                                                }
                                                const rate =
                                                    row.options.find((option) => option.id === row.selected_rate_id)
                                                        ?.rate || 0;
                                                const curRowMaxRate = Math.max(
                                                    ...row.options.map((option) => option.rate)
                                                );
                                                maxTotal = maxTotal + curRowMaxRate;

                                                individualTotal = individualTotal + parseInt(rate);
                                                submissionNumber = submissionNumber + 1;
                                                return (
                                                    <Stack
                                                        key={member.id + row.id}
                                                        alignItems={'center'}
                                                        direction={'row'}
                                                        justifyContent={'space-between'}
                                                        spacing={2}
                                                    >
                                                        <Text variant={'p2'}>{`C${index + 1} - ${findTitle}`}</Text>
                                                        <Text variant={'p2'} color={'light'}>
                                                            {' '}
                                                            {rate
                                                                ? `${_.round(rate, 2) || '0'}/${curRowMaxRate}`
                                                                : 'No submission'}
                                                        </Text>
                                                    </Stack>
                                                );
                                            }) || (
                                                <Text variant={'p2'} color={'light'}>
                                                    No Submission
                                                </Text>
                                            )}
                                            {memberSubmission && (
                                                <Stack
                                                    alignItems={'center'}
                                                    direction={'row'}
                                                    justifyContent={'flex-end'}
                                                    spacing={2}
                                                >
                                                    <Text variant={'p2'} weight={'medium'}>
                                                        Total
                                                    </Text>
                                                    <Text variant={'p2'}>{`${
                                                        _.round(individualTotal, 2) || '0'
                                                    }/${maxTotal}`}</Text>
                                                </Stack>
                                            )}
                                        </Stack>
                                    </Stack>
                                    <Stack spacing={2}>
                                        <Text variant={'p2'}>Comments</Text>
                                        <Text variant={'p2'} color={'light'}>
                                            {memberSubmission?.details || 'No submission'}
                                        </Text>
                                    </Stack>
                                </RubricMobileRateStack>
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
            </Stack>
            <Divider sx={{ margin: '24px 0px' }} />
            <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'} sx={{ marginTop: 7 }}>
                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                    <Text variant={'p2'} weight={'medium'}>
                        Individual Average
                    </Text>
                    {isNotInATeam && (
                        <Tooltip
                            maxWidth={160}
                            title={
                                <Stack divider={<Divider />} spacing={1}>
                                    <Text color={'inherit'} variant={'p3'}>
                                        Students were moved from this group after this average was calculated was
                                        calculated, as a result it is no longer accurate. Please manually review the
                                        average for this team for your records.
                                    </Text>
                                </Stack>
                            }
                            type={'alert'}
                        />
                    )}
                </Stack>
                <Text variant={'p2'} color={'light'}>
                    {findRubricAvg(selectedStudent, submissions, members)}
                </Text>
            </Stack>
            <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                <Text variant={'p2'} weight={'medium'}>
                    Team Average
                </Text>
                <Text variant={'p2'} color={'light'}>
                    {findRubricAvg(selectedStudent, submissions, members)}
                </Text>
            </Stack>
            <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                <Text variant={'p2'} weight={'medium'}>
                    Raw #
                </Text>
                <Text data-cy={'student-rubric-raw-number'} variant={'p2'} color={'light'}>
                    {findRubricRawNum(selectedStudent, submissions, members)}
                </Text>
            </Stack>
        </RubricSubmissionStack>
    );
};

export const RubricMobileGiven = ({ device, currentPeerEvaluation, selectedStudent, students, submissionList }) => {
    const student = students.find((student) => student.owner_account_id === selectedStudent);
    if (!student) return;
    const findMySubmission = submissionList.find(
        (submission) => submission.owner_account_id === student.owner_account_id
    );
    const submissionData = findMySubmission?.data || {};
    const members = students.filter((member) => member.active && member.group_id === student.group_id);
    return (
        <RubricSubmissionStack device={device} key={selectedStudent}>
            <Stack spacing={6}>
                {members.map((member) => {
                    let individualTotal = 0;
                    let submissionNumber = 0;
                    let maxTotal = 0;
                    const evaluation = submissionData?.[member.owner_account_id];
                    return (
                        <Accordion key={member.owner_account_id}>
                            <AccordionSummary>
                                <Text variant={'p1'} weight={'semibold'}>
                                    {getUserPreferredFullName(member)}
                                </Text>
                            </AccordionSummary>
                            <AccordionDetails>
                                <RubricMobileRateStack spacing={1}>
                                    <Stack spacing={2}>
                                        <Text variant={'p2'} weight={'medium'}>
                                            Criteria
                                        </Text>
                                        <Stack spacing={2}>
                                            {evaluation?.rubric_data?.map((row, index) => {
                                                let findTitle = currentPeerEvaluation.rubric_data.find(
                                                    (orgRow) => orgRow.id === row.id
                                                )?.title;
                                                if (!findTitle) {
                                                    findTitle = row.title || 'No Title';
                                                }
                                                const rate =
                                                    row.options.find((option) => option.id === row.selected_rate_id)
                                                        ?.rate || 0;

                                                const curRowMaxRate = Math.max(
                                                    ...row.options.map((option) => option.rate)
                                                );
                                                maxTotal = maxTotal + curRowMaxRate;

                                                individualTotal = individualTotal + parseInt(rate);
                                                submissionNumber = submissionNumber + 1;
                                                return (
                                                    <Stack
                                                        key={member.id + row.id}
                                                        alignItems={'center'}
                                                        direction={'row'}
                                                        justifyContent={'space-between'}
                                                        spacing={2}
                                                    >
                                                        <Text variant={'p2'}>{`C${index + 1} - ${findTitle}`}</Text>
                                                        <Text variant={'p2'} color={'light'}>
                                                            {rate
                                                                ? `${_.round(rate, 2) || '0'}/${curRowMaxRate}`
                                                                : 'No submission'}
                                                        </Text>
                                                    </Stack>
                                                );
                                            }) || (
                                                <Text variant={'p2'} color={'light'}>
                                                    No Submission
                                                </Text>
                                            )}
                                            <Stack
                                                alignItems={'center'}
                                                direction={'row'}
                                                justifyContent={'flex-end'}
                                                spacing={2}
                                            >
                                                <Text variant={'p2'} weight={'medium'}>
                                                    Total
                                                </Text>
                                                <Text variant={'p2'}>{`${
                                                    _.round(individualTotal, 2) || '0'
                                                }/${maxTotal}`}</Text>
                                            </Stack>
                                        </Stack>
                                    </Stack>
                                    <Stack spacing={2}>
                                        <Text variant={'p2'} weight={'medium'}>
                                            Comments
                                        </Text>
                                        <Text rich variant={'p2'} color={'light'}>
                                            {evaluation?.details || 'No submission'}
                                        </Text>
                                    </Stack>
                                </RubricMobileRateStack>
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
            </Stack>
        </RubricSubmissionStack>
    );
};

// Alerts
export const SUBMISSION_ALERT_TYPES = {
    RECEIVED_ZERO: 'received-zero',
    GIVEN_ZERO: 'given-zero',
    LOW: 'low',
    HIGH: 'high',
};

export const analyzeAlerts = (owner_account_id, submissions, isRubric) => {
    const findRubricProblems = () => {
        const findProblems = submissions?.reduce((alerts, submission) => {
            const eachStudentsProblem = Object.entries(submission.data).reduce((acc, [key, value]) => {
                if (key === owner_account_id) {
                    const rubricData = value.rubric_data;
                    if (rubricData) {
                        rubricData.forEach((row) => {
                            const selectedRateId = row.selected_rate_id;
                            const findSelectedRate = row.options?.find((option) => option.id === selectedRateId)?.rate;
                            if (!findSelectedRate || parseInt(findSelectedRate) === 0) {
                                const isSelfSubmission = owner_account_id === submission.owner_account_id;
                                const findVariant = isSelfSubmission
                                    ? SUBMISSION_ALERT_TYPES.GIVEN_ZERO
                                    : SUBMISSION_ALERT_TYPES.RECEIVED_ZERO;
                                const addAlert = {
                                    variant: findVariant,
                                };
                                const findDuplicate = acc.find(({ variant }) => variant === findVariant);
                                if (!findDuplicate) acc = [...acc, addAlert];

                                return true;
                            }
                            return false;
                        });
                    }
                }
                return acc;
            }, []);
            alerts = [...alerts, ...eachStudentsProblem];
            return alerts;
        }, []);
        return findProblems;
    };

    const findZeroSumProblems = () => {
        const findProblems = submissions?.reduce((alerts, submission) => {
            const eachStudentsProblem = Object.entries(submission.data).reduce((acc, [key, value]) => {
                if (key === owner_account_id) {
                    const rate = value.rate;
                    if (parseInt(rate) === 0) {
                        const isSelfSubmission = owner_account_id === submission.owner_account_id;
                        const findVariant = isSelfSubmission
                            ? SUBMISSION_ALERT_TYPES.GIVEN_ZERO
                            : SUBMISSION_ALERT_TYPES.RECEIVED_ZERO;
                        const addAlert = {
                            variant: findVariant,
                        };
                        const findDuplicate = acc.find(({ variant }) => variant === findVariant);
                        if (!findDuplicate) acc = [...acc, addAlert];
                    }
                }
                return acc;
            }, []);
            alerts = [...alerts, ...eachStudentsProblem];
            return alerts;
        }, []);
        return findProblems;
    };
    return isRubric ? findRubricProblems() : findZeroSumProblems();
};

export const RenderAlerts = (alerts = []) => {
    return (
        <AlertStack>
            {alerts.map((alert) => {
                switch (alert.variant) {
                    case SUBMISSION_ALERT_TYPES.GIVEN_ZERO: {
                        return (
                            <Stack alignItems={'center'} direction={'row'} spacing={4}>
                                <AlertIcon kind={'error'} dark={true} />
                                <Text color={'inherit'}>This student gave a zero in their peer evaluation.</Text>
                            </Stack>
                        );
                    }
                    case SUBMISSION_ALERT_TYPES.RECEIVED_ZERO: {
                        return (
                            <Stack alignItems={'center'} direction={'row'} spacing={4}>
                                <AlertIcon kind={'error'} dark={true} />
                                <Text color={'inherit'}>This student received a zero in their peer evaluation.</Text>
                            </Stack>
                        );
                    }
                    default:
                        return;
                }
            })}
        </AlertStack>
    );
};
